import { CrlItemStatus, CrlItemType, CrlRequestItem } from '../../models';

/**
 * The condition-driven alert shown at the top of a request item's Documents
 * tab. Headline + copy are system-generated from the item's state (overdue,
 * changes-requested, or both) × its kind (file upload vs text response);
 * `firmMessage` is the firm's own note, rendered italic below the copy and
 * present only when the firm has requested changes.
 *
 * Copy matrix mirrors Figma node 11939-12519 (Client Experience Revamp).
 */
export interface CrlItemAlert {
  headline: string;
  copy: string;
  /** The firm's flag note; shown italic under the copy for changes-requested cases. */
  firmMessage: string | null;
}

/** True when `dueDate` is a valid date in the past. */
function isPastDue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const due = Date.parse(dueDate);
  return !Number.isNaN(due) && due < Date.now();
}

/**
 * Resolves the item's Documents-tab alert, or `null` when the item is neither
 * overdue nor awaiting changes (no alert shown).
 *
 * - "Changes requested" = status `NEEDS_UPDATE` (the firm rejected it).
 * - "Overdue" = status `OVERDUE`, or a still-open changes-requested item whose
 *   due date has already passed (→ the combined "overdue AND changes" case).
 * - Kind: questionnaire → "text response"; everything else → "file upload".
 * - File-upload changes use singular/plural copy by flagged-file count.
 */
export function getCrlItemAlert(item: CrlRequestItem): CrlItemAlert | null {
  const isChangesRequested = item.status === CrlItemStatus.NEEDS_UPDATE;
  const isOverdue = item.status === CrlItemStatus.OVERDUE || (isChangesRequested && isPastDue(item.dueDate));

  if (!isChangesRequested && !isOverdue) return null;

  const isFileUpload = item.type !== CrlItemType.QUESTIONNAIRE;
  const firmMessage = isChangesRequested ? item.flagMessage?.trim() || null : null;

  // Both overdue AND changes requested.
  if (isOverdue && isChangesRequested) {
    return {
      headline: 'This request is overdue and has changes requested',
      copy: isFileUpload
        ? 'The due date has passed and your firm has requested revisions. Please upload a corrected version as soon as possible.'
        : 'The due date has passed and your firm has requested revisions. Please update your answer as soon as possible.',
      firmMessage,
    };
  }

  // Changes requested only.
  if (isChangesRequested) {
    let copy: string;
    if (isFileUpload) {
      const flaggedCount = (item.files ?? []).filter((file) => file.isFlagged && !file.isReference).length;
      copy =
        flaggedCount > 1
          ? 'The documents highlighted below need to be revised. Please upload corrected versions.'
          : 'The document highlighted below needs to be revised. Please upload a corrected version.';
    } else {
      copy = 'The response below needs to be revised. Please update your answer as soon as possible.';
    }
    return { headline: 'Changes requested by your firm', copy, firmMessage };
  }

  // Overdue only.
  return {
    headline: 'This request is overdue.',
    copy: isFileUpload
      ? 'The due date has passed. Please upload the requested documents as soon as possible.'
      : 'The due date has passed. Please submit your response as soon as possible.',
    firmMessage: null,
  };
}
